import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BookmarkItem, ClipPlayback, Document } from '@proref/types';
import {
  addBookmark,
  listBookmarks,
  removeBookmark,
  addDocumentBookmark,
  removeDocumentBookmark,
  updateBookmarkNote,
} from '../services/bookmarks.service';

// The Bookmarked page's list of saved clips.
export function useBookmarksQuery() {
  // Changes with the user's own save/remove actions (which already invalidate this key), so a
  // 30s stale window keeps revisits instant without hiding those changes.
  return useQuery({ queryKey: ['bookmarks'], queryFn: listBookmarks, staleTime: 30_000 });
}

// Remove a clip from the Bookmarked page — OPTIMISTICALLY. Unlike useToggleBookmark (which is
// keyed to a single clip's Save button), this drops the row straight out of the ['bookmarks']
// list so the card vanishes on click. onError restores the exact list; onSettled reconciles and
// also clears the clip's cached viewer.is_bookmarked so the player's Save button (if that clip is
// cached) reflects the removal without its own refetch. No success toast — the card disappearing
// is the confirmation; the error toast (on rollback) is raised by the caller.
export function useRemoveBookmark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clipId: string) => removeBookmark(clipId),
    onMutate: async (clipId: string) => {
      await qc.cancelQueries({ queryKey: ['bookmarks'] });
      const prev = qc.getQueryData<BookmarkItem[]>(['bookmarks']);
      qc.setQueryData<BookmarkItem[]>(['bookmarks'], (old) =>
        (old ?? []).filter((b) => b.clip?.id !== clipId),
      );
      return { prev };
    },
    onError: (_err, _clipId, ctx) => {
      if (ctx?.prev) qc.setQueryData(['bookmarks'], ctx.prev);
    },
    onSettled: (_data, _err, clipId) => {
      void qc.invalidateQueries({ queryKey: ['bookmarks'] });
      // Keep the clip's Save button (wherever it's cached) in sync with the removal.
      const apply = (old: ClipPlayback | undefined): ClipPlayback | undefined =>
        old && old.id === clipId && old.viewer
          ? { ...old, viewer: { ...old.viewer, is_bookmarked: false } }
          : old;
      qc.setQueryData<ClipPlayback>(['clip', clipId], apply);
      qc.setQueryData<ClipPlayback>(['referee-clips', 'daily'], apply);
    },
  });
}

// Toggle the Save state of a clip — OPTIMISTICALLY. The mutation takes the CURRENT bookmarked
// state and flips it. We patch the clip's cached viewer.is_bookmarked in onMutate so the Save
// button switches to "Saved" the instant it's clicked (no waiting on the request); if the call
// fails we restore the exact pre-click cache. onSettled re-confirms against the server. The clip
// lives under two possible query keys (the active-clip query and the daily clip), so we patch
// both — whichever holds this clip re-renders the button.
export function useToggleBookmark(clipId: string) {
  const qc = useQueryClient();
  const clipKey = ['clip', clipId] as const;
  const dailyKey = ['referee-clips', 'daily'] as const;

  function patchBookmarked(value: boolean) {
    const apply = (old: ClipPlayback | undefined): ClipPlayback | undefined =>
      old && old.id === clipId && old.viewer
        ? { ...old, viewer: { ...old.viewer, is_bookmarked: value } }
        : old;
    qc.setQueryData<ClipPlayback>(clipKey, apply);
    qc.setQueryData<ClipPlayback>(dailyKey, apply);
  }

  return useMutation({
    mutationFn: (isBookmarked: boolean) => (isBookmarked ? removeBookmark(clipId) : addBookmark(clipId)),
    onMutate: async (isBookmarked: boolean) => {
      // Cancel in-flight refetches so a slow response can't overwrite the optimistic value.
      await qc.cancelQueries({ queryKey: clipKey });
      await qc.cancelQueries({ queryKey: ['referee-clips'] });
      const snapshot = {
        clip: qc.getQueryData<ClipPlayback>(clipKey),
        daily: qc.getQueryData<ClipPlayback>(dailyKey),
      };
      patchBookmarked(!isBookmarked); // instant flip
      return snapshot;
    },
    onError: (_err, _vars, ctx) => {
      // Restore the exact pre-click cache — the button snaps back to its real state.
      if (!ctx) return;
      qc.setQueryData(clipKey, ctx.clip);
      qc.setQueryData(dailyKey, ctx.daily);
    },
    onSettled: () => {
      // Reconcile with the server (and refresh the Bookmarked list). Since the cache already
      // matches the optimistic value on success, this refetch is invisible.
      void qc.invalidateQueries({ queryKey: clipKey });
      void qc.invalidateQueries({ queryKey: ['referee-clips'] });
      void qc.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });
}

// Remove a DOCUMENT bookmark from the Bookmarked page — OPTIMISTICALLY (drops the row on click),
// mirroring useRemoveBookmark for clips. No success toast; the caller raises the error on rollback.
export function useRemoveDocumentBookmark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => removeDocumentBookmark(documentId),
    onMutate: async (documentId: string) => {
      await qc.cancelQueries({ queryKey: ['bookmarks'] });
      const prev = qc.getQueryData<BookmarkItem[]>(['bookmarks']);
      qc.setQueryData<BookmarkItem[]>(['bookmarks'], (old) =>
        (old ?? []).filter((b) => b.document?.id !== documentId),
      );
      return { prev };
    },
    onError: (_err, _documentId, ctx) => {
      if (ctx?.prev) qc.setQueryData(['bookmarks'], ctx.prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['bookmarks'] });
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

// Set/clear a bookmark's personal note. Patches the note into the cached ['bookmarks'] list on
// success (a targeted write — no full refetch flicker of the editor being typed in).
export function useUpdateBookmarkNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => updateBookmarkNote(id, note),
    onSuccess: (updated) => {
      qc.setQueryData<BookmarkItem[]>(['bookmarks'], (old) =>
        (old ?? []).map((b) => (b.id === updated.id ? { ...b, note: updated.note } : b)),
      );
    },
  });
}

// Toggle a document's Save state from the Documents library — OPTIMISTICALLY, mirroring
// useToggleBookmark for clips. Patches the document's cached is_bookmarked in the ['documents']
// list so the Save button flips instantly; onError restores; onSettled reconciles + refreshes the
// Bookmarked list so a saved doc appears there.
export function useToggleDocumentBookmark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isBookmarked }: { id: string; isBookmarked: boolean }) =>
      isBookmarked ? removeDocumentBookmark(id) : addDocumentBookmark(id),
    onMutate: async ({ id, isBookmarked }) => {
      await qc.cancelQueries({ queryKey: ['documents'] });
      // The documents list is cached under ['documents', sportId], so patch every matching query
      // (getQueriesData/setQueriesData work by prefix) and snapshot them for rollback.
      const prev = qc.getQueriesData<Document[]>({ queryKey: ['documents'] });
      qc.setQueriesData<Document[]>({ queryKey: ['documents'] }, (old) =>
        (old ?? []).map((d) => (d.id === id ? { ...d, is_bookmarked: !isBookmarked } : d)),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      for (const [key, data] of ctx?.prev ?? []) qc.setQueryData(key, data);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      void qc.invalidateQueries({ queryKey: ['bookmarks'] });
    },
  });
}
